import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';

import {resetPasswordValidationSchema} from 'form/validation/register';
import {Link} from 'react-router-dom';
import PasswordChecklist from 'react-password-checklist';

import Loader from 'components/ui/AmnisLoader/AmnisLoader';
import SubmitButton from 'components/ui/Button/SubmitButton';
import UnauthorizedPageTitle from 'components/ui/UnauthorizedPageTitle/UnauthorizedPageTitle';
import {parseJwt} from 'helpers/common';

import {useSetPassword} from 'api/users';
import {handleFormError} from 'helpers/handleFormError';

import {PATH} from 'constants/common';
import {translate} from 'helpers/localize';
import styles from './ResetPassword.module.scss';
import TextInput from 'components/ui/Form/TextInput/TextInput';
import Form, {FormOnSubmit} from 'components/ui/Form/Form/Form';
import AmnisButton from 'components/ui/AmnisButton/AmnisButton';
import Alert from 'components/ui/Alert/Alert';

interface IFormValues {
    password: string;
    confirmPassword: string;
}

const ResetPassword: React.FC<{token: string}> = ({token}) => {
    const {mutate: setPassword, isPending: isLoading} = useSetPassword();
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

    const formMethods = useForm<IFormValues>({
        resolver: yupResolver(resetPasswordValidationSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    const {watch} = formMethods;

    const {password, confirmPassword} = watch();

    const onSubmit: FormOnSubmit<IFormValues> = (values, setError) => {
        const decodedToken = parseJwt(token);
        setPassword(
            {
                plainPassword: values.password,
                token,
                userId: decodedToken['@id'],
            },
            {
                onSuccess: () => setIsSuccess(true),
                onError: requestErrors =>
                    handleFormError(requestErrors, setError),
            },
        );
    };

    if (isLoading) {
        return <Loader />;
    }

    if (isSuccess) {
        return (
            <Alert
                variant="success"
                className={styles.alertPw}
                message={
                    <>
                        {translate(
                            'You have successfully changed your password. Please ',
                        ) ||
                            'You have successfully changed your password. Please '}
                        <span
                            style={{
                                fontWeight: 'bold',
                                color: '#0099da',
                                cursor: 'pointer',
                            }}
                        >
                            <Link to={PATH.LOGIN}>
                                {translate('Login.') || 'Login.'}
                            </Link>
                        </span>
                    </>
                }
            />
        );
    }

    return (
        <>
            <UnauthorizedPageTitle
                pageTitle={<span>Set your password</span>}
                titleTag="Set your password"
                subtitle="minimum requirements:"
            />
            <PasswordChecklist
                rules={['minLength', 'number', 'capital', 'match']}
                minLength={8}
                value={password}
                valueAgain={confirmPassword}
                onChange={isValid => {
                    if (isValid) setIsSubmitDisabled(false);
                    else setIsSubmitDisabled(true);
                }}
                className="mb-3 ml-3"
            />
            <Form formMethods={formMethods} onSubmit={onSubmit}>
                <TextInput
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    rounded={false}
                    size="large"
                />
                <TextInput
                    label="Password"
                    name="confirmPassword"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    rounded={false}
                    size="large"
                />
                <AmnisButton
                    isLoading={isLoading}
                    disabled={isSubmitDisabled}
                    type="submit"
                >
                    Reset Password
                </AmnisButton>
            </Form>
        </>
    );
};

export default ResetPassword;
